import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { superadminAuthService } from '../services/superadminAuthService';

import type {
  AuthContextType,
  AuthState,
  AuthAction,
  LoginCredentials,
} from '@/types/auth';

// Initial auth state for superadmin
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token || null,
        isAuthenticated: true,
        loading: false,
        error: null,
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload,
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };

    case 'AUTH_RESTORE':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token || null,
        isAuthenticated: true,
        loading: false,
        error: null,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
}

// Create Superadmin Auth Context
const SuperadminAuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Props
interface SuperadminAuthProviderProps {
  children: React.ReactNode;
}

// Superadmin Auth Provider Component
export const SuperadminAuthProvider: React.FC<SuperadminAuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Restore auth on mount
  useEffect(() => {
    restoreAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login function for superadmin
  const login = useCallback(async (credentials: LoginCredentials) => {
    dispatch({ type: 'AUTH_START' });
    try {
      // Call the superadmin login API
      const authData = await superadminAuthService.login(credentials);

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: authData.user,
          token: null, // Using cookies now, no token in state
        },
      });

      toast.success(`${authData.data.message}!`);
      return;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: message });
      toast.error(message);
      throw error;
    }
  }, []);

  // Logout function for superadmin
  const logout = useCallback(async () => {
    dispatch({ type: 'AUTH_START' });

    try {
      // Call the superadmin logout API
      const response = await superadminAuthService.logout();

      dispatch({ type: 'AUTH_LOGOUT' });
      
      // Show success message from API response
      toast.success(response.message || 'Logged out successfully');
    } catch (error) {
      // Always logout locally even if server request fails
      dispatch({ type: 'AUTH_LOGOUT' });
      
      const message = error instanceof Error ? error.message : 'Logout failed';
      console.warn('Logout request failed:', message);
      
      // Still show a success message to user since we cleared local data
      toast.success('Logged out successfully');
    }
  }, []);

  // Restore auth from localStorage/cookies
  const restoreAuth = useCallback(async () => {
    dispatch({ type: 'AUTH_START' });

    try {
      const storedAuth = superadminAuthService.getStoredAuth();

      // Temporarily only check localStorage since server cookie is not working
      // TODO: Re-enable cookie check once server cookie issue is fixed
      if (storedAuth.user && superadminAuthService.isAuthenticated()) {
        // Restore from localStorage
        dispatch({
          type: 'AUTH_RESTORE',
          payload: {
            user: storedAuth.user,
            token: null,
          },
        });

        // Fetch fresh user details from the new API
        try {
          await superadminAuthService.getCurrentUserDetails();
          // Update the user data in context with fresh data
          const { user: updatedUser } = superadminAuthService.getStoredAuth();
          if (updatedUser) {
            dispatch({
              type: 'AUTH_RESTORE',
              payload: {
                user: updatedUser,
                token: null,
              },
            });
          }
        } catch (error) {
          console.warn('Failed to fetch current user details:', error);
          // Don't fail auth restoration if this fails
        }
        return;
      }

      // Cookie check temporarily disabled
      // if (storedAuth.user && !superadminAuthService.hasAuthCookie()) {
      //   superadminAuthService.clearStoredAuth();
      //   dispatch({ type: 'AUTH_LOGOUT' });
      //   return;
      // }

      dispatch({ type: 'AUTH_LOGOUT' });
    } catch {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  // Refresh token function (placeholder)
  const refreshToken = useCallback(async () => {
    // Implement if needed for superadmin
    return Promise.resolve();
  }, []);

  // Listen for auth logout events
  useEffect(() => {
    const handleAuthLogout = () => {
      dispatch({ type: 'AUTH_LOGOUT' });
      localStorage.removeItem('superadmin_auth_user');
      // Redirect to superadmin login page
      window.location.href = '/superadmin/auth/login';
    };

    window.addEventListener('superadmin:auth:logout', handleAuthLogout);

    return () => {
      window.removeEventListener('superadmin:auth:logout', handleAuthLogout);
    };
  }, []);

  // Context value
  const contextValue: AuthContextType = {
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    login,
    register: async () => {
      throw new Error('Registration not available for superadmin');
    },
    logout,
    forgotPassword: async () => {
      throw new Error('Forgot password not available for superadmin');
    },
    resetPassword: async () => {
      throw new Error('Reset password not available for superadmin');
    },
    changePassword: async () => {
      throw new Error('Change password not available for superadmin');
    },
    updateProfile: async () => {
      throw new Error('Update profile not available for superadmin');
    },
    refreshToken,
    restoreAuth,
  };

  return (
    <SuperadminAuthContext.Provider value={contextValue}>{children}</SuperadminAuthContext.Provider>
  );
};

// Custom hook to use superadmin auth context
// eslint-disable-next-line react-refresh/only-export-components
export const useSuperadminAuth = (): AuthContextType => {
  const context = useContext(SuperadminAuthContext);

  if (context === undefined) {
    throw new Error('useSuperadminAuth must be used within a SuperadminAuthProvider');
  }

  return context;
};

// Hook to get current superadmin user
// eslint-disable-next-line react-refresh/only-export-components
export const useSuperadminUser = () => {
  const { user, loading, isAuthenticated } = useSuperadminAuth();
  return { user, loading, isAuthenticated };
};

// Hook for superadmin auth status
// eslint-disable-next-line react-refresh/only-export-components
export const useSuperadminAuthStatus = () => {
  const { isAuthenticated, loading } = useSuperadminAuth();
  return { isAuthenticated, loading };
};

